import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../config/api";
import { setSessionToken } from "../../utils/authSession";
import { useTranslate } from "../../Hook/useTranslate";
import FormBanner from "../Common/FormBanner";
import loginBg from "../../assets/login-background.jpg";
import logo from "../../assets/waseeny-logo.png";
/* SVG Icons */
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/*Waseeny Logo */
const WaseenyLogo = () => (
  <svg width="230" height="84" viewBox="0 0 230 84" xmlns="http://www.w3.org/2000/svg">
    {/* Speed streaks */}
    <line x1="2" y1="37" x2="14" y2="37" stroke="#E31E24" strokeWidth="2.8" strokeLinecap="round" />
    <line x1="4" y1="42" x2="14" y2="42" stroke="#E31E24" strokeWidth="2" strokeLinecap="round" />
    <line x1="6" y1="32" x2="14" y2="32" stroke="#E31E24" strokeWidth="2" strokeLinecap="round" />

    {/* Dome */}
    <ellipse cx="40" cy="26" rx="24" ry="18" fill="#E31E24" />
    <rect x="16" y="26" width="48" height="6" rx="1" fill="#E31E24" />
    {/* HALAL text */}
    <text x="40" y="17" textAnchor="middle" fill="white" fontSize="5.5" fontWeight="700" fontFamily="Arial">100% HALAL</text>
    {/* Dome gloss */}
    <ellipse cx="40" cy="19" rx="14" ry="7" fill="white" opacity="0.12" />
    {/* Cart body */}
    <rect x="13" y="32" width="55" height="18" rx="4" fill="#111111" />
    <line x1="19" y1="37" x2="62" y2="37" stroke="#2a2a2a" strokeWidth="1.2" />
    <line x1="19" y1="41" x2="62" y2="41" stroke="#2a2a2a" strokeWidth="1.2" />
    {/* Handle */}
    <rect x="66" y="22" width="4.5" height="24" rx="2.2" fill="#111111" />
    {/* Wheels */}
    <circle cx="26" cy="54" r="8" fill="#111" stroke="#555" strokeWidth="1.8" />
    <circle cx="26" cy="54" r="4" fill="#2a2a2a" />
    <circle cx="26" cy="54" r="1.5" fill="#555" />
    <circle cx="55" cy="54" r="8" fill="#111" stroke="#555" strokeWidth="1.8" />
    <circle cx="55" cy="54" r="4" fill="#2a2a2a" />
    <circle cx="55" cy="54" r="1.5" fill="#555" />

    {/* Arabic*/}
    <text x="228" y="30" textAnchor="end" fill="#111" fontSize="17" fontWeight="700" fontFamily="'Arial', sans-serif">وصيني</text>

    {/* Waseeny wordmark */}
    <text x="84" y="60" fill="#E31E24" fontSize="32" fontWeight="900" fontFamily="'Georgia','Times New Roman',serif" letterSpacing="-1">W</text>
    <text x="107" y="60" fill="#111111" fontSize="32" fontWeight="900" fontFamily="'Georgia','Times New Roman',serif" letterSpacing="-1">aseeny</text>

    {/* Tagline */}
    <text x="84" y="74" fill="#888" fontSize="7.8" fontFamily="Arial" letterSpacing="2.2">||| DELIVERY AT HOME |||</text>
  </svg>
);

/* Main Component */
export default function WaseenyLogin({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/home";
  const t = useTranslate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [btnPress, setBtnPress] = useState(false);
  const [ripple, setRipple] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  useEffect(() => {
    const initGoogle = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: "369059791436-v692mmlaa0nisnrcm2ldoj3dii841o6f.apps.googleusercontent.com",
          callback: handleGoogleResponse,
        });

        window.google.accounts.id.renderButton(
          document.getElementById("googleBtnDiv"),
          {
            theme: "outline",
            size: "large",
            width: "100%"
          }
        );
      }
    };

    initGoogle();

    // Re-check every second if the button didn't render (e.g. script was still loading)
    const interval = setInterval(() => {
      const btn = document.getElementById("googleBtnDiv");
      if (window.google && btn && !btn.hasChildNodes()) {
        initGoogle();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!email.trim()) {
      setFormError(t("enterEmailError") || "Please enter your email address");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email.trim())) {
      setFormError(t("enterValidEmailError") || "Please enter a valid email address");
      return;
    }
    if (!password) {
      setFormError(t("enterPasswordError") || "Please enter your password");
      return;
    }

    setLoading(true);

    try {
      const res = await api.post("/auth/login", {
        email: email.trim(),
        password,
      });


      // token
      const token = res.data.token;

      if (!token) {
        setFormError("Token not received from backend");
        return;
      }

      setSessionToken(token);
      if (res.data.user) {
        localStorage.setItem("user", JSON.stringify(res.data.user));
      }

      setIsAuthenticated(true);
      navigate(from, { replace: true });

    } catch (err) {
      setFormError("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };
  const handleGoogleResponse = async (response) => {
    if (!response || !response.credential) {
      console.warn("Google login response missing credential:", response);
      setFormError("Google login token missing. Please try clicking the Google button again.");
      return;
    }
    setFormError("");
    setLoading(true);

    try {
      const res = await api.post("/auth/google", { token: response.credential });

      if (res.data.needsMobile) {
        // if User doesn't have a mobile number yet, redirect to add-mobile
        sessionStorage.setItem("pendingGoogleCredential", response.credential);
        sessionStorage.setItem("pendingGoogleRedirect", from);
        navigate("/add-mobile", { state: { from, googleSignIn: true } });
      } else if (res.data.token) {
        //if User is fully registered and has a mobile number, log in directly
        setSessionToken(res.data.token);
        setIsAuthenticated(true);
        navigate(from, { replace: true });
      } else {
        setFormError("Something went wrong with Google Login");
      }
    } catch (err) {
      console.error("Google Login Error:", err.response?.data || err);
      setFormError(err.response?.data?.message || "Google login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/*Global styles */}
      <button onClick={() => navigate(-1)} className="back-btn-login">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        body { overflow: hidden; }

        .back-btn-login {
          position: absolute;
          top: 30px;
          left: 30px;
          z-index: 10;
          background: rgba(255,255,255,0.8);
          border: 1px solid #e2e8f0;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justifyContent: center;
          cursor: pointer;
          color: #111;
          transition: all 0.2s;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }
        .back-btn-login:hover {
          background: #fff;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.1);
        }

        .wf-display { font-family: 'Playfair Display', Georgia, serif !important; }
        .wf-body    { font-family: 'DM Sans', system-ui, sans-serif !important; }

        /* ── Entrance animations ── */
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(32px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        @keyframes logoIn {
          from { opacity: 0; transform: translateY(-20px) scale(0.88); }
          to   { opacity: 1; transform: translateY(0)     scale(1);    }
        }
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }

        /* ── Button ripple ── */
        @keyframes ripple {
          from { transform: scale(0);  opacity: 0.45; }
          to   { transform: scale(5);  opacity: 0;    }
        }
        /* ── Floating food particles ── */
        @keyframes floatUp {
          0%,100% { transform: translateY(0px) rotate(0deg);   opacity: 0.18; }
          50%     { transform: translateY(-18px) rotate(8deg); opacity: 0.28; }
        }
        }
        @keyframes floatDown {
          0%,100% { transform: translateY(0px) rotate(0deg);  opacity: 0.15; }
          50%     { transform: translateY(14px) rotate(-6deg); opacity: 0.22; }
        }
        /* ── Button pulse ── */
        @keyframes redGlow {
          0%,100% { box-shadow: 0 8px 32px rgba(227,30,36,0.42), 0 2px 8px rgba(227,30,36,0.25); }
          50%     { box-shadow: 0 8px 48px rgba(227,30,36,0.60), 0 2px 12px rgba(227,30,36,0.35); }
        }
        /* ── Focus ring grow ── */
        @keyframes focusRing {
          from { box-shadow: 0 0 0 0 rgba(227,30,36,0.3); }
          to   { box-shadow: 0 0 0 4px rgba(227,30,36,0.15); }
        }

        /* ── Assigned delays ── */
        .a-logo  { animation: logoIn  0.75s cubic-bezier(.34,1.56,.64,1) 0.00s both; }
        .a-h1    { animation: slideUp 0.60s ease                         0.20s both; }
        .a-sub   { animation: slideUp 0.55s ease                         0.30s both; }
        .a-f1    { animation: slideUp 0.55s ease                         0.40s both; }
        .a-f2    { animation: slideUp 0.55s ease                         0.50s both; }
        .a-btn   { animation: slideUp 0.55s ease                         0.62s both; }
        .a-or    { animation: fadeIn  0.5s  ease                         0.74s both; }
        .a-g1    { animation: slideUp 0.50s ease                         0.82s both; }
        .a-g2    { animation: slideUp 0.50s ease                         0.92s both; }

        /* ── Input styles ── */
        .w-input {
          width: 100%;
          background: rgba(255,255,255,0.94);
          border: 1.8px solid rgba(210,210,210,0.7);
          border-radius: 14px;
          padding: 15px 18px;
          font-size: 15px;
          color: #1a1a1a;
          font-family: 'DM Sans', system-ui, sans-serif;
          font-weight: 400;
          outline: none;
          transition: border-color 0.24s, box-shadow 0.24s, background 0.2s;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }
        .w-input:focus {
          border-color: #E31E24;
          background: rgba(255,255,255,1);
          animation: focusRing 0.28s forwards;
        }
        .w-input::placeholder { color: #b0b0b0; font-weight: 400; }

        /* ── Google button ── */
        .g-btn {
          width: 100%;
          background: rgba(255,255,255,0.92);
          border: 1.8px solid rgba(210,210,210,0.65);
          border-radius: 14px;
          padding: 14px 20px;
          display: flex; align-items: center; justify-content: center; gap: 12px;
          cursor: pointer;
          font-family: 'DM Sans', system-ui, sans-serif;
          font-size: 15px; font-weight: 500; color: #333;
          transition: background 0.22s, box-shadow 0.22s, transform 0.18s, border-color 0.2s;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          position: relative; overflow: hidden;
        }
        .g-btn:hover {
          background: rgba(255,255,255,1);
          box-shadow: 0 6px 24px rgba(0,0,0,0.10);
          transform: translateY(-2px);
          border-color: rgba(180,180,180,0.9);
        }
        .g-btn:active { transform: translateY(0); box-shadow: none; }

        /* ── Red CTA ── */
        .cta-btn {
          width: 100%;
          background: linear-gradient(140deg, #E31E24 0%, #c8181d 55%, #b01419 100%);
          border: none; border-radius: 50px;
          padding: 18px 20px;
          color: white;
          font-family: 'DM Sans', system-ui, sans-serif;
          font-size: 16px; font-weight: 700; letter-spacing: 0.4px;
          cursor: pointer;
          position: relative; overflow: hidden;
          transition: transform 0.18s, filter 0.18s;
          animation: redGlow 2.5s ease-in-out infinite;
        }
        .cta-btn:hover  { transform: translateY(-2px) scale(1.01); filter: brightness(1.07); }
        .cta-btn:active { transform: translateY(1px)  scale(0.99); filter: brightness(0.95); }

        .cta-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          animation: none;
          transform: none;
        }

        .ripple-circle {
          position: absolute;
          background: rgba(255,255,255,0.38);
          border-radius: 50%;
          width: 40px; height: 40px;
          transform: scale(0);
          animation: ripple 0.65s ease-out forwards;
          pointer-events: none;
        }

        /* ── Divider ── */
        .divider-line {
          flex: 1; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(0,0,0,0.16), transparent);
        }

        /* ── Eye toggle ── */
        .eye-btn {
          position: absolute; right: 16px; top: 50%;
          transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: #b0b0b0; display: flex; align-items: center;
          padding: 4px;
          transition: color 0.2s, transform 0.2s;
        }
        .eye-btn:hover { color: #E31E24; transform: translateY(-50%) scale(1.15); }

        /* ── Forgot link ── */
        .forgot-link {
          font-family: 'DM Sans', system-ui, sans-serif;
          font-size: 14px; font-weight: 500; color: #444;
          text-decoration: none;
          transition: color 0.2s;
          display: inline-block;
          position: relative;
        }
        .forgot-link::after {
          content: ''; position: absolute; bottom: -1px; left: 0; right: 0;
          height: 1px; background: #E31E24;
          transform: scaleX(0); transform-origin: right;
          transition: transform 0.25s ease;
        }
        .forgot-link:hover { color: #E31E24; }
        .forgot-link:hover::after { transform: scaleX(1); transform-origin: left; }

        /* ── Floating leaf/spice emojis ── */
        .fp { position: absolute; font-size: 28px; pointer-events: none; user-select: none; }
        .fp1  { top:  8%; left:  6%; animation: floatUp   6.0s ease-in-out infinite; }
        .fp2  { top: 12%; right: 5%; animation: floatDown 7.5s ease-in-out infinite; }
        .fp3  { bottom: 10%; left: 8%; animation: floatDown 5.5s ease-in-out infinite; }
        .fp4  { bottom: 15%; right: 7%; animation: floatUp   8.0s ease-in-out infinite; }
      `}</style>

      {/* ── Full-screen container ── */}
      <div
        className="wf-body"
        style={{
          position: "relative",
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          padding: "24px 16px",
        }}
      >
        {/* Background food photo */}
        <div
          style={{
            position: "absolute", inset: 0,
            // backgroundImage: `url('https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1600&q=80&auto=format&fit=crop')`,
            backgroundImage: `url(${loginBg})`,
            backgroundSize: "cover", backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            zIndex: 0,
          }}
        />

        {/* Cream radial wash  matches screenshot's airy feel */}
        <div
          style={{
            position: "absolute", inset: 0, zIndex: 1,
            background: "radial-gradient(ellipse 90% 90% at 50% 48%, rgba(255,253,248,0.86) 0%, rgba(255,251,244,0.76) 38%, rgba(255,248,238,0.50) 70%, rgba(255,245,230,0.22) 100%)",
          }}
        />
        {/* Top fade */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: "160px", zIndex: 2,
          background: "linear-gradient(to bottom, rgba(255,252,248,0.94) 0%, transparent 100%)"
        }} />
        {/* Bottom fade */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: "120px", zIndex: 2,
          background: "linear-gradient(to top, rgba(255,252,248,0.90) 0%, transparent 100%)"
        }} />

        {/* Floating decorative herbs/spices */}
        <span className="fp fp1">🌿</span>
        <span className="fp fp2">🍃</span>
        <span className="fp fp3">✨</span>
        <span className="fp fp4">🌱</span>

        {/* Card */}
        <div
          style={{
            position: "relative", zIndex: 10,
            width: "100%", maxWidth: "610px",
          }}
        >
          {/* Logo */}
          <div
            className={`${mounted ? "a-logo" : ""}`}
            style={{ display: "flex", justifyContent: "center", marginBottom: "10px" }}
          >
            <img
              src={logo}
              alt="logo"
              style={{ height: "80px", objectFit: "contain" }}
            />
          </div>

          {/* Headline */}
          <div className={`${mounted ? "a-h1" : ""}`}
            style={{ textAlign: "center", marginBottom: "6px" }}>
            <h1 className="wf-display"
              style={{
                fontSize: "clamp(28px,5vw,40px)", fontWeight: 800, color: "#111",
                letterSpacing: "-0.4px", lineHeight: 1.18
              }}>
              {t("welcomeBack")}
            </h1>
          </div>

          {/* Sub-heading */}
          <div className={`${mounted ? "a-sub" : ""}`}
            style={{ textAlign: "center", marginBottom: "32px" }}>
            <p style={{ fontSize: "16px", color: "#6b6b6b", fontWeight: 400, lineHeight: 1.5 }}>
              {t("loginSubtitle")}
            </p>
          </div>

          <FormBanner
            message={formError}
            type="error"
            onDismiss={() => setFormError("")}
          />

          {/* Email field */}
          <div className={`${mounted ? "a-f1" : ""}`} style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block", fontSize: "14px", fontWeight: 600,
              color: "#1a1a1a", marginBottom: "9px"
            }}>
              {t("emailLabel")}
            </label>
            <input
              type="email"
              className="w-input"
              value={email}
              onChange={e => { setEmail(e.target.value); setFormError(""); }}
              placeholder={t("emailPlaceholder")}
            />
          </div>

          {/*Password field*/}
          <div className={`${mounted ? "a-f2" : ""}`} style={{ marginBottom: "6px" }}>
            <label style={{
              display: "block", fontSize: "14px", fontWeight: 600,
              color: "#1a1a1a", marginBottom: "9px"
            }}>
              {t("passwordLabel")}
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPass ? "text" : "password"}
                className="w-input"
                value={password}
                onChange={e => { setPassword(e.target.value); setFormError(""); }}
                placeholder={t("passwordPlaceholder")}
                style={{ paddingRight: "52px" }}
              />
              <button className="eye-btn" onClick={() => setShowPass(v => !v)}>
                {showPass ? <EyeIcon /> : <EyeOffIcon />}
              </button>
            </div>
            {/* Forgot password */}
            <div style={{ textAlign: "right", marginTop: "10px" }}>
              <a href="#" className="forgot-link" onClick={(e) => {
                e.preventDefault();
                navigate("/forgot-password");
              }}>{t("forgotPassword")}</a>
            </div>
          </div>

          {/*  Get Started CTA*/}
          <div className={`${mounted ? "a-btn" : ""}`} style={{ marginTop: "20px" }}>
            <button
              className="cta-btn"
              onClick={handleSubmit}
              disabled={loading}
              onMouseDown={() => setBtnPress(true)}
              onMouseUp={() => setBtnPress(false)}
            >
              {!loading && ripple && (
                <span
                  className="ripple-circle"
                  style={{ left: ripple.x - 20, top: ripple.y - 20 }}
                />
              )}
              {loading ? t("loggingIn") : t("getStarted")}
            </button>
          </div>

          {/* OR divider  */}
          <div className={`${mounted ? "a-or" : ""}`}
            style={{
              display: "flex", alignItems: "center", gap: "14px",
              margin: "22px 0"
            }}>
            <div className="divider-line" />
            <span style={{
              color: "#aaa", fontSize: "14px", fontWeight: 400,
              whiteSpace: "nowrap", letterSpacing: "0.2px"
            }}>
              {t("or")}
            </span>
            <div className="divider-line" />
          </div>

          {/* Social buttons*/}
          <div className={`${mounted ? "a-g1" : ""}`} style={{ marginBottom: "14px" }}>
            <div id="googleBtnDiv"></div>
          </div>

          <p style={{ textAlign: "center", marginTop: "16px" }}>
            {t("dontHaveAccount")}{" "}
            <span
              style={{ color: "red", cursor: "pointer" }}
              onClick={() => navigate("/register")}
            >
              {t("signUp")}
            </span>
          </p>
        </div>
      </div>
    </>
  );
}
